import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Loader,
  Heading,
  View,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import { LayoutContext } from "layout/context/LayoutContext";
import { lotPhotosByLotID, cropUnitsByLotID } from "graphql/queries";

import AgroLocationSelectors from "./components/AgroLocationSelectors";
import LotDemarcationCanvas from "components/agro/lots/LotDemarcationCanvas";

const AgroDashboard = () => {
  const { me } = useContext(LayoutContext);

  // Selection State
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedFarmId, setSelectedFarmId] = useState("");
  const [selectedLotId, setSelectedLotId] = useState("");

  // Canvas Data State
  const [imageUrl, setImageUrl] = useState(null);
  const [cropUnits, setCropUnits] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Fetch data when Lot changes
  useEffect(() => {
    if (!selectedLotId) {
      setImageUrl(null);
      setCropUnits([]);
      return;
    }

    const fetchData = async () => {
      setLoadingData(true);
      try {
        await Promise.all([loadLotPhoto(selectedLotId), loadCropUnits(selectedLotId)]);
      } catch (error) {
        console.error("Error loading dashboard data", error);
        toast.error(strings.responses.errorLoadData);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [selectedLotId]);

  // --- Data Fetching Logic ---

  async function loadLotPhoto(lotId) {
    try {
      const res = await API.graphql(
        graphqlOperation(lotPhotosByLotID, { lotID: lotId })
      );
      const items = res?.data?.lotPhotosByLotID?.items ?? [];
      if (items.length) {
        const photo = items[0];
        const keyOrUrl = photo.key ?? photo.url ?? "";
        if (!keyOrUrl) {
          setImageUrl(null);
          return;
        }
        if (keyOrUrl.startsWith("http")) {
          setImageUrl(keyOrUrl);
        } else {
          try {
            const resolved = await Storage.get(keyOrUrl, { level: "public" });
            setImageUrl(resolved);
          } catch (e) {
            console.warn("Could not resolve stored photo key", e);
            setImageUrl(keyOrUrl);
          }
        }
      } else {
        setImageUrl(null);
      }
    } catch (err) {
      console.error("Error loading LotPhoto", err);
      throw err;
    }
  }

  async function loadCropUnits(lotId) {
    try {
      const res = await API.graphql(
        graphqlOperation(cropUnitsByLotID, {
          lotID: lotId,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const items = res?.data?.cropUnitsByLotID?.items ?? [];
      setCropUnits(items);
    } catch (err) {
      console.error("Error loading crop units", err);
      throw err;
    }
  }

  // Handlers for Selectors
  const onSelectCompany = (id) => setSelectedCompanyId(id);
  const onSelectFarm = (id) => setSelectedFarmId(id);
  const onSelectLot = (id) => setSelectedLotId(id);

  return (
    <div>
      <span className="fs-3">{strings.title}</span>

      <Grid
        className="p-3"
        templateColumns={{ base: "1fr", large: "1fr 1fr 1fr" }}
        gap="15px"
        marginBottom="20px"
      >
        <AgroLocationSelectors
          selectedCompanyId={selectedCompanyId}
          selectedFarmId={selectedFarmId}
          selectedLotId={selectedLotId}
          onSelectCompany={onSelectCompany}
          onSelectFarm={onSelectFarm}
          onSelectLot={onSelectLot}
        />
      </Grid>

      {selectedLotId && (
        <LotDemarcationCanvas
          imageUrl={imageUrl}
          cropUnits={cropUnits}
          readOnly
          isLoadingData={loadingData}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Agro - Dashboard",
    responses: {
      errorLoadData: "Error al cargar datos del lote"
    },
  },
});

export default AgroDashboard;
